import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { RepairshoprTicketLineItem } from '@techgitter/contracts';
import { useInvoiceCreation } from '../../hooks/useInvoiceCreation';

interface TicketInvoicePromptProps {
  open: boolean;
  onClose: () => void;
  lineItems: RepairshoprTicketLineItem[];
  rsCustomerId: number;
  rsTicketId: number;
  dvJobGuid: string;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(amount);
}

export const TicketInvoicePrompt: React.FC<TicketInvoicePromptProps> = ({
  open,
  onClose,
  lineItems,
  rsCustomerId,
  rsTicketId,
  dvJobGuid,
}) => {
  const navigate = useNavigate();
  const { phase, invoiceNumber, error, submit, reset } = useInvoiceCreation();

  // Unpaid (unconverted) line items
  const unpaidItems = useMemo(
    () => lineItems.filter((li) => !li.converted),
    [lineItems],
  );

  // Checkbox state — all checked by default
  const [checkedIds, setCheckedIds] = useState<Set<number>>(
    () => new Set(unpaidItems.map((li) => li.id)),
  );

  // Reset checked when dialog opens with new items
  React.useEffect(() => {
    if (open) {
      setCheckedIds(new Set(unpaidItems.map((li) => li.id)));
    }
  }, [open, unpaidItems]);

  const toggleItem = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleCreate = useCallback(async () => {
    // Pass the UNCHECKED unpaid items so the orchestrator can delete the
    // corresponding (RS-auto-copied) invoice line items after creation.
    const excludedTicketLineItems = unpaidItems
      .filter((li) => !checkedIds.has(li.id))
      .map((li) => ({
        name: li.name,
        description: li.description,
        quantity: Number(li.quantity) || 1,
        price: li.price_retail,
      }));
    await submit({
      rsCustomerId,
      rsTicketId,
      dvJobGuid,
      excludedTicketLineItems: excludedTicketLineItems.length
        ? excludedTicketLineItems
        : undefined,
    });
  }, [submit, rsCustomerId, rsTicketId, dvJobGuid, unpaidItems, checkedIds]);

  // Navigate on success. RS auto-copies the ticket's line items and the
  // orchestrator removes any excluded ones before syncing to DV.
  React.useEffect(() => {
    if (phase === 'succeeded' && invoiceNumber) {
      handleClose();
      navigate(`/invoice/${invoiceNumber}`);
    }
  }, [phase, invoiceNumber, navigate, handleClose]);

  const isWorking = phase === 'submitting' || phase === 'accepted';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Invoice</DialogTitle>
      <DialogContent>
        {unpaidItems.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select line items to add to the new invoice:
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafbfd' }}>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontSize: 13, fontWeight: 'bold' }}>Item</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 'bold', width: 60 }}>Qty</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 'bold', width: 80, textAlign: 'right' }}>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unpaidItems.map((li) => (
                    <TableRow key={li.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checkedIds.has(li.id)}
                          onChange={() => toggleItem(li.id)}
                          disabled={isWorking}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 14 }}>{li.name}</TableCell>
                      <TableCell sx={{ fontSize: 14 }}>{li.description}</TableCell>
                      <TableCell sx={{ fontSize: 14 }}>{li.quantity}</TableCell>
                      <TableCell sx={{ fontSize: 14, textAlign: 'right' }}>
                        {formatCurrency(li.price_retail)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No unconverted line items. An empty invoice will be created.
          </Typography>
        )}

        {isWorking && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {phase === 'submitting' ? 'Submitting...' : 'Creating invoice...'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isWorking}
        >
          Create Invoice
        </Button>
      </DialogActions>
    </Dialog>
  );
};
